import { Bot, FileText, Megaphone, User as UserIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { CampaignActivityEvent, CampaignActivityEventKind } from "./context";

interface Props {
  events: CampaignActivityEvent[];
}

export function CampaignActivityTimeline({ events }: Props) {
  if (events.length === 0) {
    return <p className="text-sm text-text-muted">No activity recorded yet.</p>;
  }

  return (
    <ol className="relative space-y-4 border-l border-ring pl-6" aria-label="Campaign activity">
      {events.map((event) => (
        <li key={event.id} className="relative">
          <span
            className={`absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full ${iconBackground(event)}`}
            aria-hidden="true"
          >
            {iconFor(event)}
          </span>
          <div className="rounded-lg bg-card p-3 shadow-sm ring-1 ring-ring">
            <p className="text-sm font-medium text-text-primary">{event.summary}</p>
            <p className="mt-0.5 text-xs text-text-muted">
              {event.actor ? event.actor.name : "System"}
              {" · "}
              {formatTimestamp(event.occurredAt)}
            </p>
            {renderExtras(event)}
          </div>
        </li>
      ))}
    </ol>
  );
}

function iconFor(event: CampaignActivityEvent): ReactNode {
  switch (event.kind) {
    case "stage_change": {
      const isAuto = Boolean(event.details.auto);
      return isAuto ? <Bot size={12} /> : <UserIcon size={12} />;
    }
    case "brief_version":
      return <FileText size={12} />;
    case "job_posting_version":
      return <Megaphone size={12} />;
  }
}

function iconBackground(event: CampaignActivityEvent): string {
  switch (event.kind) {
    case "stage_change":
      return event.details.auto ? "bg-blue-50 text-blue-700" : "bg-accent-light text-amber-800";
    case "brief_version":
      return "bg-emerald-50 text-emerald-700";
    case "job_posting_version":
      return "bg-violet-50 text-violet-700";
  }
}

function renderExtras(event: CampaignActivityEvent): ReactNode {
  const summary = event.details.change_summary;
  if (typeof summary === "string" && summary.trim().length > 0) {
    return <p className="mt-1 text-xs italic text-text-muted">"{summary}"</p>;
  }
  return null;
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export type { CampaignActivityEventKind };
